import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, DoCheck, ElementRef, EventEmitter, OnChanges, OnDestroy, OnInit, Output, QueryList, SimpleChanges, ViewChild, ViewChildren } from '@angular/core';
import { ModalDismissReasons, NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslatePipe } from '@ngx-translate/core';
import { SharedModule } from 'src/app/shared.module';

@Component({
  selector: 'app-camera-capture',
  templateUrl: './camera-capture.component.html',
  styleUrls: ['./camera-capture.component.scss'],
  providers: [NgbModal],
  standalone: true,
  imports: [CommonModule, TranslateModule],
})
export class CameraCaptureComponent  {
  @Output() imageCaptured = new EventEmitter<string>();
  @ViewChild('video') video: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') canvas: ElementRef<HTMLCanvasElement>;
  
  stream: MediaStream | null = null;
  capturedImage: string | null = null;
  videoDevices: MediaDeviceInfo[] = [];
  currentDeviceId: string | null | undefined = null;
  canSwitchCamera: boolean = false;
  facingMode: 'user' | 'environment' = 'environment';
  isLoadingCamera: boolean = false; // For loading state in UI
  errMessage: string | null = null;

  constructor(private modalService: NgbModal, private activeModal: NgbActiveModal) { }

  ngAfterViewInit() {
    setTimeout(() => this.initializeCamera(), 0);
  }

  // Initialize camera by verifying access first
  async initializeCamera() {
    this.isLoadingCamera = true;
    await this.getVideoDevices();
    const success = await this.verifyCameraAccess();
    if (success) {
      await this.startCamera(this.currentDeviceId);
    } else {
      alert('Unable to access any camera. Please use the upload option.');
    }
    this.isLoadingCamera = false;
  }

  // Verify access to all cameras before starting
  async verifyCameraAccess(maxRetries = 3, retryDelayMs = 100, timeoutMs = 5000): Promise<boolean> {
    const startTime = Date.now();
    let backCamera: MediaDeviceInfo | undefined;

    // Find back camera
    backCamera = this.videoDevices.find(device => device.label.toLowerCase().includes('back') || device.label.toLowerCase().includes('rear'));

    if (!backCamera) {
      console.warn('No back camera found, falling back to front camera');
      this.facingMode = 'user';
      this.currentDeviceId = this.videoDevices.find(device => device.label.toLowerCase().includes('front'))?.deviceId || null;
      return this.videoDevices.length > 0; // Return true if any camera is available
    }

    // Try accessing back camera until successful or timeout
    while (Date.now() - startTime < timeoutMs) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const constraints: MediaStreamConstraints = {
            video: { deviceId: { exact: backCamera.deviceId } },
          };
          const testStream = await navigator.mediaDevices.getUserMedia(constraints);
          testStream.getTracks().forEach(track => track.stop()); // Clean up test stream
          console.log(`Back camera access verified: ${backCamera.deviceId}`);
          this.currentDeviceId = backCamera.deviceId;
          this.facingMode = 'environment';
          return true;
        } catch (err:any) {
          console.error(`Back camera attempt ${attempt} failed: ${err.name} - ${err.message}`);
          if (err.name === 'NotReadableError' && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            continue;
          }
          break;
        }
      }
    }

    // Back camera failed, try front camera
    console.warn('Back camera inaccessible, falling back to front camera');
    this.facingMode = 'user';
    const frontCamera = this.videoDevices.find(device => device.label.toLowerCase().includes('front'));
    if (frontCamera) {
      while (Date.now() - startTime < timeoutMs) {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          const constraints: MediaStreamConstraints = {
            video: { deviceId: { exact: frontCamera.deviceId } },
          };
          const testStream = await navigator.mediaDevices.getUserMedia(constraints);
          testStream.getTracks().forEach(track => track.stop()); // Clean up test stream
          console.log(`Front camera access verified: ${frontCamera.deviceId}`);
          this.currentDeviceId = frontCamera.deviceId;
          this.facingMode = 'environment';
          return true;
        } catch (err:any) {
          console.error(`Front camera attempt ${attempt} failed: ${err.name} - ${err.message}`);
          if (err.name === 'NotReadableError' && attempt < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, retryDelayMs));
            continue;
          }
          break;
        }
      }
    }
    }

    return false; // No cameras accessible
  }

  async startCamera(deviceId?: string | null): Promise<boolean> {
    if (!this.video?.nativeElement) {
      console.error('Video element not found');
      return false;
    }
    try {
      // Stop any existing stream
      this.stopStream();

      // Request camera access
      const constraints: MediaStreamConstraints = {
        video: deviceId
          ? { deviceId: { exact: deviceId } }
          : {
              facingMode: { ideal: this.facingMode },
              width: { ideal: 1920 },
              height: { ideal: 1080 },
            },
      };
      this.stream = await navigator.mediaDevices.getUserMedia(constraints);
      this.video.nativeElement.srcObject = this.stream;
      // Enforce resolution if supported
      const [track] = this.stream.getVideoTracks();
      const settings = track.getSettings();
      const capabilities = track.getCapabilities?.();
      console.log(`Camera started: ${settings.width}x${settings.height}, facing: ${settings.facingMode || this.facingMode}`);
      if (capabilities?.width && capabilities?.height) {
        const desiredWidth = Math.min(2560, capabilities.width.max || 2560);
        const desiredHeight = Math.min(1920, capabilities.height.max || 1920);
        try{
          await track.applyConstraints({
            width: 2560,
            height: 1920,
          });
          console.log(`Resolution applied successfully: ${desiredWidth}x${desiredHeight}`);
        }
        catch(applyErr: any){ 
          console.warn('Failed to apply high resolution (fallback to default):', applyErr.name);
        };
      }
      this.currentDeviceId = deviceId || settings.deviceId || null;
      console.log(`Camera started with deviceId: ${this.currentDeviceId}, facingMode: ${this.facingMode}`);
      this.errMessage = null; 
      return true;
    } catch (err:any) {
      console.error('Error starting camera:', err.name, err.message);
      switch (err.name) {
        case 'NotFoundError':
          this.errMessage = 'No camera found. Please check your device settings.';
        break;
        case 'NotReadableError':
          this.errMessage = 'Camera is already in use by another application.';
        break;
        case 'OverconstrainedError':
          this.errMessage = 'Requested resolution or camera constraints not supported.';
        break;
        case 'NotAllowedError':
          this.errMessage = 'Camera access denied. Please allow access in browser settings.';
        break;
        default:
          this.errMessage = 'An unexpected camera error occurred.';
      }
      
      // ✅ Automatic fallback (try basic camera)

      if (err.name === 'OverconstrainedError') {
        console.warn('Retrying camera with relaxed constraints...');
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({ video: true });
          this.stream = fallbackStream;
          this.video.nativeElement.srcObject = fallbackStream;
          console.log('Fallback camera started successfully.');
          return true;
        } catch (fallbackErr:any) {
          console.error('Fallback also failed:', fallbackErr.name, fallbackErr.message);
        }
      }
      return false;
    }
  }

  async getVideoDevices(maxRetries = 3, retryDelayMs = 500) {
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Try both facing modes to ensure all cameras are enumerated
        await Promise.all([
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } }).catch(() => {}),
          navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' } }).catch(() => {}),
        ]);
        const devices = await navigator.mediaDevices.enumerateDevices();
        this.videoDevices = devices.filter(device => device.kind === 'videoinput' && device.deviceId);
        console.log('Available video devices:', this.videoDevices.map(d => ({ label: d.label, deviceId: d.deviceId })));
        if (this.videoDevices.length > 0) {
          this.canSwitchCamera = this.videoDevices.length > 1;
          return;
        }
        console.warn(`Device enumeration attempt ${attempt} returned no valid devices`);
      } catch (err: any) {
        console.error(`Device enumeration attempt ${attempt} failed: ${err.name} - ${err.message}`);
      }
      if (attempt < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, retryDelayMs));
      }
    }
    console.error('Failed to enumerate devices after retries');
    this.canSwitchCamera = false;
    this.videoDevices = [];
  }

  async switchCamera() {
    if (!this.canSwitchCamera || this.videoDevices.length <= 1) {
      console.warn('No additional cameras available');
      return;
    }

    this.isLoadingCamera = true;
    // try {
    //   const currentIndex = this.videoDevices.findIndex(device => device.deviceId === this.currentDeviceId);
    //   const nextIndex = (currentIndex + 1) % this.videoDevices.length;
    //   const nextDeviceId = this.videoDevices[nextIndex].deviceId;
    //   this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    //   console.log(`Switching to camera: ${this.videoDevices[nextIndex].label}, deviceId: ${nextDeviceId}, facingMode: ${this.facingMode}`);

    //   // Verify access to the next camera before starting
    //   const testConstraints: MediaStreamConstraints = {
    //     video: { deviceId: { exact: nextDeviceId } },
    //   };
    //   const testStream = await navigator.mediaDevices.getUserMedia(testConstraints);
    //   testStream.getTracks().forEach(track => track.stop());
    //   const success = await this.startCamera(nextDeviceId);
    //   if (!success) {
    //     console.warn('Switch failed, retrying with facingMode');
    //     await this.startCamera();}
    // } catch (err:any) {
    //   console.error('Error switching camera:', err.name, err.message);
      
    //   await this.startCamera();
    // }
    this.facingMode = this.facingMode === 'user' ? 'environment' : 'user';
    await this.startCamera();
    this.isLoadingCamera = false;
  }

  captureImage(){
    const context = this.canvas.nativeElement.getContext('2d');
    const targetWidth = 2560;
    const targetHeight = 1920;
    this.canvas.nativeElement.width = targetWidth;
    this.canvas.nativeElement.height = targetHeight;
    context?.drawImage(this.video.nativeElement, 0, 0, targetWidth, targetHeight);
    this.capturedImage = this.canvas.nativeElement.toDataURL('image/jpeg', 0.8);
    this.stopStream();
  }

  retakeImage() {
    this.capturedImage = null;
    this.startCamera(this.currentDeviceId);
  }

  saveImage(){
    if (this.capturedImage) {
      console.log("image to emit:", this.capturedImage)
      this.imageCaptured.emit(this.capturedImage);
      this.closeModal();
    }
  }
  
  closeModal() {
    if (this.modalService.hasOpenModals()) {
      this.activeModal.close(this.capturedImage)
    }
    this.stopStream();
    this.capturedImage = null;
  }

  stopStream() {
    if (this.stream) {
    // Stop all tracks
    this.stream.getTracks().forEach(track => {
      track.stop(); // Stop the track
      track.enabled = false; // Disable the track
      console.log(`Stopped track: ${track.kind} - ${track.id}`);
    });
    
    // Explicitly release the stream
    this.stream = null;
    console.log('Stream released');
  }

  // Clear the video element's srcObject
  if (this.video?.nativeElement) {
    this.video.nativeElement.srcObject = null;
    console.log('Video element srcObject cleared');
  }
  }

  ngOnDestroy() {
    this.stopStream();
  }
}
